// Stable slot IDs, independent from the renderer, for future catalog extensions.
export const MASCOT_OPTIONS = {
  species: {
    dog: 'Cachorrinho',
    cat: 'Gatinho',
    jaguar: 'Onça-pintada',
    sheep: 'Ovelhinha Agroplay',
  },
  pose: { sitting: 'Sentadinho', standing: 'Em pé' },
  color: {
    caramel: 'Caramelo',
    cream: 'Creme',
    gray: 'Cinza',
    chocolate: 'Chocolate',
    black: 'Pretinho',
    white: 'Neve',
    ginger: 'Ruivinho',
    silver: 'Prateado',
    honey: 'Mel',
    rose: 'Rosa algodão',
    lilac: 'Lilás',
    blue: 'Azul nuvem',
    mint: 'Verde menta',
  },
  pattern: {
    plain: 'Liso',
    patches: 'Manchinhas',
    stripes: 'Listras',
    spots: 'Pintinhas',
    tuxedo: 'Frajolinha',
    blaze: 'Faixa na testa',
    freckles: 'Sardinhas',
    rosettes: 'Rosetas da onça',
    fleece: 'Lã fofinha',
  },
  accessory: {
    none: 'Sem acessório',
    bandana: 'Lenço',
    collar: 'Coleira',
    bow: 'Lacinho',
    scarf: 'Cachecol',
    bowtie: 'Gravatinha',
    pearls: 'Colar de pérolas',
    harness: 'Peitoral com coração',
    agroplay_bandana: 'Lenço Agroplay',
  },
  headwear: {
    none: 'Sem enfeite',
    flower: 'Flor na orelha',
    crown: 'Coroazinha',
    beret: 'Boininha',
    party: 'Chapéu de festa',
    star: 'Presilha de estrela',
    ranch_hat: 'Chapéu de fazendeira',
  },
  toy: {
    none: 'Sem brinquedo',
    bone: 'Ossinho',
    ball: 'Bolinha',
    yarn: 'Novelo',
    mouse: 'Ratinho',
    duck: 'Patinho',
    star: 'Estrela de pelúcia',
    fish: 'Peixinho',
    rope_ring: 'Argola de corda',
    flower_ball: 'Bola de margaridas',
    plush_sheep: 'Ovelhinha de pelúcia',
  },
  backdrop: {
    classic: 'Clássico',
    meadow: 'Jardim suave',
    sunset: 'Pôr do sol',
    clouds: 'Nas nuvens',
    stars: 'Céu estrelado',
    jungle: 'Selva nativa',
    farm: 'Fazendinha',
  },
} as const

export const MASCOT_COLOR_FIELDS = {
  furColor: 'Cor livre do pelo',
  eyeColor: 'Cor dos olhos',
  accessoryColor: 'Cor do acessório',
  headwearColor: 'Cor do enfeite',
  toyColor: 'Cor do brinquedo',
  sceneColor: 'Cor do cenário',
} as const
export type MascotColorSlot = keyof typeof MASCOT_COLOR_FIELDS
export const MASCOT_COLOR_DEFAULTS: Record<MascotColorSlot, string> = {
  furColor: '#cb945d',
  eyeColor: '#815b43',
  accessoryColor: '#c86662',
  headwearColor: '#e6bc65',
  toyColor: '#c97164',
  sceneColor: '#dbe8d5',
}
export const isMascotColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)

export type MascotSlot = keyof typeof MASCOT_OPTIONS
export type MascotConfig = { version: 1; name: string } & {
  [K in MascotSlot]: keyof (typeof MASCOT_OPTIONS)[K]
} & Partial<Record<MascotColorSlot, string>>
export const MASCOT_SECTIONS: Record<MascotSlot, string> = {
  species: 'Seu companheiro',
  pose: 'Pose',
  color: 'Cor do pelo',
  pattern: 'Pelagem',
  accessory: 'Acessório',
  toy: 'Brinquedo',
  headwear: 'Enfeite da cabeça',
  backdrop: 'Cenário',
}
export const MASCOT_COLORS = {
  caramel: '#CB945D',
  cream: '#E8D8B8',
  gray: '#969EA7',
  chocolate: '#85604E',
  black: '#454951',
  white: '#eee9dd',
  ginger: '#c66b36',
  silver: '#b6bdcb',
  honey: '#e4b451',
  rose: '#dca6b4',
  lilac: '#b0a0d0',
  blue: '#91b8d2',
  mint: '#9cbea6',
} as const
export const DEFAULT_MASCOT: MascotConfig = {
  version: 1,
  name: '',
  species: 'dog',
  pose: 'sitting',
  color: 'caramel',
  pattern: 'plain',
  accessory: 'bandana',
  toy: 'none',
  headwear: 'none',
  backdrop: 'classic',
}

export const SHEEP_DEFAULTS = {
  species: 'sheep',
  pose: 'standing',
  color: 'cream',
  pattern: 'fleece',
  accessory: 'agroplay_bandana',
  headwear: 'ranch_hat',
  backdrop: 'farm',
  toy: 'none',
} as const satisfies Partial<MascotConfig>

export const JAGUAR_DEFAULTS = {
  species: 'jaguar',
  color: 'honey',
  pattern: 'rosettes',
  backdrop: 'jungle',
} as const satisfies Partial<MascotConfig>

export const SHEEP_TOYS: MascotConfig['toy'][] = [
  'none',
  'rope_ring',
  'flower_ball',
  'plush_sheep',
  'ball',
  'star',
  'duck',
]

export function parseMascotName(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const name = value.trim()
  if (value !== '' && name === '') return null
  if (
    [...name].length > 24 ||
    [...name].some((char) => {
      const code = char.codePointAt(0) ?? 0
      return char === '<' || char === '>' || code < 32 || code === 127
    })
  )
    return null
  return name
}

export function parseMascotConfig(value: unknown): MascotConfig | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const input = value as Record<string, unknown>
  const keys = Object.keys(input)
  if (
    input.version !== 1 ||
    keys.some(
      (key) =>
        key !== 'version' &&
        key !== 'name' &&
        !Object.prototype.hasOwnProperty.call(MASCOT_OPTIONS, key) &&
        !Object.prototype.hasOwnProperty.call(MASCOT_COLOR_FIELDS, key)
    )
  )
    return null
  const name = parseMascotName(input.name === undefined ? '' : input.name)
  if (name === null) return null
  const result: Record<string, unknown> = { version: 1, name }
  for (const slot of Object.keys(MASCOT_OPTIONS) as MascotSlot[]) {
    const option =
      input[slot] === undefined && (slot === 'headwear' || slot === 'backdrop')
        ? DEFAULT_MASCOT[slot]
        : input[slot]
    if (
      typeof option !== 'string' ||
      !Object.prototype.hasOwnProperty.call(MASCOT_OPTIONS[slot], option)
    )
      return null
    result[slot] = option
  }
  for (const slot of Object.keys(MASCOT_COLOR_FIELDS) as MascotColorSlot[]) {
    if (input[slot] === undefined) continue
    if (!isMascotColor(input[slot])) return null
    result[slot] = input[slot]
  }
  return result as MascotConfig
}
